// row 117 Stages: a journey variant of equal segments, each with its status name and its Active Time in days; done
// segments are blue, the active one glows, the next ones sit on --card-2. The last segment takes the outcome word
// (Accepted / Declined) once there is one. Each segment keeps at least 104 px: a narrower card scrolls the strip
// inside it, as a focusable region. A done segment also says *Completed* to a screen reader, so done and next differ
// by more than colour (unless its label already says it).

import { cx } from "../lib/cx";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { vars } from "../lib/vars";
import { PROGRESS_STATE_WORDS, STAGES_TEMPLATES } from "./labels";
import { regionProps, useOverflow } from "../lib/useOverflow";

export type StagesState = "done" | "active" | "next";

export interface StagesItem {
  /** The status name, from a labels module (the offer chain). */
  label: string;
  /** Active Time spent in it, in days. */
  days?: number | undefined;
  /** Default next. */
  state?: StagesState | undefined;
}

export interface StagesProps {
  items: readonly StagesItem[];
  /** The strip's name. */
  label: string;
  /** The last segment's word once the outcome is known. */
  outcome?: string | undefined;
  className?: string | undefined;
}

export function Stages({ items, label, outcome, className }: StagesProps) {
  const { ref, overflows } = useOverflow<HTMLDivElement>();
  return (
    <div ref={ref} className={cx("mc-stages-w", className)} {...regionProps(overflows, label)}>
      <ol className="mc-stages" aria-label={label} style={vars({ "--mc-cols": Math.max(1, items.length) })}>
        {items.map((item, index) => {
          const state = item.state ?? "next";
          const last = index === items.length - 1;
          const word = last && outcome !== undefined ? outcome : item.label;
          return (
            <li key={`${item.label}-${index}`} className={cx("mc-stage", `mc-stage-${state}`)} aria-current={state === "active" ? "step" : undefined}>
              <b>{word}</b>
              {state === "done" && word !== PROGRESS_STATE_WORDS.completed ? <VisuallyHidden>{", "}{PROGRESS_STATE_WORDS.completed}</VisuallyHidden> : null}
              {item.days !== undefined ? <span>{(item.days === 1 ? STAGES_TEMPLATES.activeDay : STAGES_TEMPLATES.activeDays)({ n: item.days })}</span> : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
